/**
 * @fileoverview Syndicator Showdown — Notification Card
 * @module components/notifications/NotificationCard
 *
 * Reusable premium dark racing card used to present a single notification.
 * Purely presentational — it renders whatever `title`/`body`/`ctaLabel` the
 * Firestore doc carries, so it works for every future notification type
 * (upcoming runner, race result, stable share, system announcement) without
 * changes. Decorative artwork is a CSS background layer behind the text
 * (never baked-in text), and gracefully falls back to a gradient-only look
 * if the artwork file for that `imageType` doesn't exist yet.
 */

import React from 'react';
import {
  resolveImageType,
  getNotificationArtwork,
  getNotificationIcon,
  getNotificationEyebrow,
} from '../../utils/notificationHelpers';
import './NotificationCard.css';

export default function NotificationCard({
  notification,
  onDismiss,
  onCta,
  onLater,
  showLater = true,
}) {
  if (!notification) return null;

  const imageType = resolveImageType(notification);
  const artwork    = getNotificationArtwork(imageType);
  const icon       = getNotificationIcon(imageType);
  const eyebrow    = getNotificationEyebrow(imageType);
  const hasCta     = !!(notification.ctaLabel && notification.ctaUrl);

  const style = artwork ? { '--notification-artwork': `url('${artwork}')` } : undefined;

  return (
    <div
      className={`notif-card notif-card--${imageType}`}
      style={style}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="notif-card__close"
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        ✕
      </button>

      <div className="notif-card__content">
        <span className="notif-card__eyebrow">
          <span className="notif-card__eyebrow-icon">{icon}</span>
          {eyebrow}
        </span>

        {notification.title && (
          <h4 className="notif-card__title">{notification.title}</h4>
        )}

        {notification.body && (
          <p className="notif-card__body">{notification.body}</p>
        )}

        {(hasCta || (showLater && onLater)) && (
          <div className="notif-card__actions">
            {hasCta && (
              <button type="button" className="notif-card__cta" onClick={onCta}>
                {notification.ctaLabel}
              </button>
            )}
            {showLater && onLater && (
              <button type="button" className="notif-card__later" onClick={onLater}>
                Later
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
